/**
 * Utility functions for calendar integration
 */

// Format date for calendar events (YYYYMMDD)
const formatCalendarDate = (dateString, timeString) => {
  const [month, day, year] = dateString.split(' ')[0].split('/');
  return `${year}${month.padStart(2, '0')}${day.padStart(2, '0')}`;
};

// Format time for calendar events (HHMMSS)
const formatCalendarTime = (timeString) => {
  // Extract start time
  const startTime = timeString.split(' - ')[0];
  const [hours, minutes] = startTime.split(':');
  const isPM = startTime.includes('PM');
  
  // Convert to 24-hour format
  let hour = parseInt(hours);
  if (isPM && hour < 12) hour += 12;
  if (!isPM && hour === 12) hour = 0;
  
  return `${hour.toString().padStart(2, '0')}${minutes.padStart(2, '0')}00`;
};

// Generate Google Calendar URL
export const generateGoogleCalendarUrl = (event) => {
  if (!event) return '';

  let startDateTime, endDateTime;

  // Handle different event data formats
  if (event.startDate && event.endDate) {
    // New format: startDate and endDate as Date objects or ISO strings
    startDateTime = event.startDate?.toDate ? event.startDate.toDate() : new Date(event.startDate);
    endDateTime = event.endDate?.toDate ? event.endDate.toDate() : new Date(event.endDate);
  } else if (event.date && event.time) {
    // Legacy format: date and time strings
    const eventDate = event.date;
    const eventTime = event.time;
    if (!eventTime || !eventTime.includes(' - ')) {
      console.warn('Invalid event time format:', eventTime);
      return '';
    }
    const [startTime, endTime] = eventTime.split(' - ');

    // Format date and time for Google Calendar
    startDateTime = new Date(eventDate + ' ' + startTime);
    endDateTime = new Date(eventDate + ' ' + endTime);
  } else {
    console.warn('Invalid event format for calendar URL:', event);
    return '';
  }

  // Create URL parameters
  const params = new URLSearchParams({
    action: 'TEMPLATE',
    text: event.title,
    details: event.description,
    location: event.address || event.location,
    dates: `${startDateTime.toISOString().replace(/-|:|\.\d+/g, '')}/${endDateTime.toISOString().replace(/-|:|\.\d+/g, '')}`
  });

  return `https://calendar.google.com/calendar/render?${params.toString()}`;
};

// Generate Outlook Calendar URL
export const generateOutlookCalendarUrl = (event) => {
  if (!event) return '';

  let startDateTime, endDateTime;

  // Handle different event data formats
  if (event.startDate && event.endDate) {
    // New format: startDate and endDate as Date objects or ISO strings
    startDateTime = event.startDate?.toDate ? event.startDate.toDate() : new Date(event.startDate);
    endDateTime = event.endDate?.toDate ? event.endDate.toDate() : new Date(event.endDate);
  } else if (event.date && event.time) {
    // Legacy format: date and time strings
    const eventDate = event.date;
    const eventTime = event.time;
    if (!eventTime || !eventTime.includes(' - ')) {
      console.warn('Invalid event time format:', eventTime);
      return '';
    }
    const [startTime, endTime] = eventTime.split(' - ');

    // Format date and time for Outlook Calendar
    startDateTime = new Date(eventDate + ' ' + startTime);
    endDateTime = new Date(eventDate + ' ' + endTime);
  } else {
    console.warn('Invalid event format for calendar URL:', event);
    return '';
  }

  // Create URL parameters
  const params = new URLSearchParams({
    path: '/calendar/action/compose',
    rru: 'addevent',
    subject: event.title,
    body: event.description,
    location: event.address || event.location,
    startdt: startDateTime.toISOString(),
    enddt: endDateTime.toISOString()
  });

  return `https://outlook.office.com/calendar/0/deeplink/compose?${params.toString()}`;
};

// Generate iCalendar file content
export const generateICalendarContent = (event) => {
  if (!event) return '';

  let startDateTime, endDateTime;

  // Handle different event data formats
  if (event.startDate && event.endDate) {
    // New format: startDate and endDate as Date objects or ISO strings
    startDateTime = event.startDate?.toDate ? event.startDate.toDate() : new Date(event.startDate);
    endDateTime = event.endDate?.toDate ? event.endDate.toDate() : new Date(event.endDate);
  } else if (event.date && event.time) {
    // Legacy format: date and time strings
    const eventDate = event.date;
    const eventTime = event.time;
    if (!eventTime || !eventTime.includes(' - ')) {
      console.warn('Invalid event time format:', eventTime);
      return '';
    }
    const [startTime, endTime] = eventTime.split(' - ');

    // Format date and time for iCalendar
    startDateTime = new Date(eventDate + ' ' + startTime);
    endDateTime = new Date(eventDate + ' ' + endTime);
  } else {
    console.warn('Invalid event format for calendar content:', event);
    return '';
  }
  
  // Format dates for iCalendar
  const formatICalDate = (date) => {
    return date.toISOString().replace(/-|:|\.\d+/g, '');
  };
  
  // Create iCalendar content
  const icalContent = [
    'BEGIN:VCALENDAR',
    'VERSION:2.0',
    'PRODID:-//APN//Event Calendar//EN',
    'CALSCALE:GREGORIAN',
    'BEGIN:VEVENT',
    `SUMMARY:${event.title}`,
    `DTSTART:${formatICalDate(startDateTime)}`,
    `DTEND:${formatICalDate(endDateTime)}`,
    `LOCATION:${event.address || event.location}`,
    `DESCRIPTION:${event.description.replace(/\\n/g, '\\n')}`,
    'END:VEVENT',
    'END:VCALENDAR'
  ].join('\r\n');
  
  return icalContent;
};

// Download iCalendar file
export const downloadICalendarFile = (event) => {
  if (!event) return;
  
  const icalContent = generateICalendarContent(event);
  const blob = new Blob([icalContent], { type: 'text/calendar;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  
  // Create a link and trigger download
  const link = document.createElement('a');
  link.href = url;
  link.download = `${event.title.replace(/\s+/g, '_')}.ics`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  
  // Clean up
  URL.revokeObjectURL(url);
};
